import React from "react";
import { Helmet } from "react-helmet-async";
import { getSiteOrigin } from "@/siteOrigin";

/**
 * SEO component using react-helmet-async.
 * Provides per-page <title>, meta description, OG, Twitter, canonical, hreflang (fr-CA/en-CA/x-default),
 * geo meta tags, and optional JSON-LD schema for rich-result eligibility.
 */
export default function SEO({
  title,
  description,
  path = "/",
  image,
  lang = "fr",
  schema, // optional JSON-LD object or array
}) {
  const fullTitle = title
    ? `${title} | Phaneroo Prod — Moncton, NB`
    : lang === "fr"
      ? "Production Vidéo & Photo Cinématique Moncton NB | Phaneroo Prod"
      : "Video & Photo Production Moncton NB | Phaneroo Prod";

  const origin = getSiteOrigin();
  const url = `${origin}${path.startsWith("/") ? path : `/${path}`}`;
  const ogImage = image || "/generated/founder_cinema_camera.jpg";
  const absImage = ogImage.startsWith("http") ? ogImage : `${origin}${ogImage.startsWith("/") ? ogImage : `/${ogImage}`}`;
  const desc = description ||
    (lang === "fr"
      ? "Studio de production vidéo, photo et contenu cinématique à Moncton, NB. Bilingue FR/EN. Films de marque, photographie, drone aérien partout au Canada Atlantique."
      : "Cinematic video and photography production studio in Moncton, NB. Bilingual FR/EN. Brand films, photography, drone aerial across Atlantic Canada.");

  // Build alternate URLs — same URL for both languages (content switches client-side)
  const enUrl = url;
  const frUrl = url;

  return (
    <Helmet>
      <html lang={lang === "fr" ? "fr-CA" : "en-CA"} />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="geo.region" content="CA-NB" />
      <meta name="geo.placename" content="Moncton, New Brunswick, Canada" />
      <meta name="geo.position" content="46.0878;-64.7782" />
      <meta name="ICBM" content="46.0878, -64.7782" />
      <link rel="canonical" href={url} />
      {/* hreflang: bilingual CA + x-default fallback */}
      <link rel="alternate" hrefLang="fr-CA" href={frUrl} />
      <link rel="alternate" hrefLang="en-CA" href={enUrl} />
      <link rel="alternate" hrefLang="x-default" href={url} />
      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Phaneroo Prod" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={absImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Phaneroo Prod — cinematic production studio, Moncton NB" />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content={lang === "fr" ? "fr_CA" : "en_CA"} />
      <meta property="og:locale:alternate" content={lang === "fr" ? "en_CA" : "fr_CA"} />
      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@phanerooprod" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={absImage} />
      {/* JSON-LD */}
      {schema && (Array.isArray(schema) ? schema : [schema]).map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}
